import { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import {
  Select,
  Checkbox,
  InputText,
  Currency,
  IconSearch,
  InputSize,
  PaymentType as PaymentTypeProps,
  SelectItemProps,
  Autocomplete,
  IconAnimatedDots,
  PermissionEnum,
} from '@ui/src';
import { PaymentType } from '../../../store/dictionaries/types';
import { CreateExternalAccountTypes } from './types';

import cn from './styles.module.css';

const ExternalAccountCreateForm = ({
  control,
  errors,
  pspPartnerId,
  paymentType,
  paymentTypes,
  setSelectedPartner,
  pspPartners,
  selectedPartner,
  setValue,
  currencyId,
  accounts,
  balances,
  casp,
  accountId,
  isOpenPayd,
  integrationType,
  setIntegrationType,
  openPaydIntegration,
  balancesIsLoading,
  creationType,
  setCreationType,
  activePermissions,
  setBindOrganizationPopupVisible,
  pspIntegrationOrganization,
  organizationPspExternalAccountsList,
  externalAccountsIsLoading,
  externalAccountsIsFetching,
  selectedExternalOrganizationAccount,
  setSelectedExternalOrganizationAccount,
  bindIsNotAvailalbe,
  setBindIsNotAvailable,
  primeVaultExternalId,
  setPrimeVaultExternalId,
  isPrimeVault,
  caspId,
  caspValues,
  setIsPrimeVault,
  setIsOpenPayd,
  setPanelIsOpen,
  isKeel,
  setIsKeel,
}: CreateExternalAccountTypes) => {
  const [filteredPaymentTypes, setFilteredPaymentTypes] = useState<
    PaymentType[]
  >([]);
  const [currencies, setCurrencies] = useState<Currency[]>();
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentType>();
  const [searchValue, setSearchValue] = useState('');
  const [availableCurrencies, setAvailableCurrencies] = useState<Currency[]>(
    [],
  );
  const [caspList, setCaspList] = useState<SelectItemProps[]>([]);

  const onSearch = (value: string) => {
    const nextCurrencies: Currency[] = value
      ? availableCurrencies?.filter((item) =>
          item.name.toLowerCase().includes(value.toLowerCase()),
        )
      : availableCurrencies?.map((item) => item);

    setSearchValue(value);

    setCurrencies(nextCurrencies);
  };

  useEffect(() => {
    if (selectedPartner && paymentTypes) {
      const nextPaymentTypes: PaymentType[] = [];

      paymentTypes?.forEach((item) => {
        if (
          selectedPartner?.paymentsTypes?.find(
            (type) => type === item.paymentType,
          )
        ) {
          nextPaymentTypes.push(item);
        }
      });

      setFilteredPaymentTypes(nextPaymentTypes);
    }
  }, [paymentTypes, selectedPartner]);

  useEffect(() => {
    if (
      (isOpenPayd || isKeel) &&
      integrationType === 'with-integration' &&
      openPaydIntegration &&
      balances
    ) {
      const integrationCurrencies: { name: string; id: string }[] = [];

      openPaydIntegration?.pspIntegrationPayments?.forEach((item) => {
        item.currencies?.forEach((currencyItem) => {
          if (
            !integrationCurrencies?.find(
              (integrationCurrency) =>
                integrationCurrency.id === currencyItem.id,
            )
          ) {
            integrationCurrencies.push(currencyItem.currency);
          }
        });
      });

      if (integrationCurrencies?.length) {
        const nextCurrenciesList: Currency[] = [];

        integrationCurrencies?.forEach((item) => {
          if (
            balances?.find((balance) => balance.currencyId === item.id) &&
            !nextCurrenciesList.find((currency) => currency.id === item.id)
          ) {
            nextCurrenciesList.push(item as Currency);
          }
        });

        setCurrencies(nextCurrenciesList);
        setAvailableCurrencies(nextCurrenciesList);
      }
    }
  }, [isOpenPayd, isKeel, integrationType, openPaydIntegration, balances]);

  useEffect(() => {
    if (
      filteredPaymentTypes?.length &&
      paymentType &&
      selectedPaymentType &&
      ((!isOpenPayd && !isKeel) ||
        ((isOpenPayd || isKeel) && integrationType === 'without-integration'))
    ) {
      const nextCurrenciesList: Currency[] = [];

      selectedPaymentType?.currencies?.edges?.forEach((item) => {
        if (balances?.find((balance) => balance.currencyId === item.node.id)) {
          nextCurrenciesList.push(item.node);
        }
      });

      setCurrencies(nextCurrenciesList);
      setAvailableCurrencies(nextCurrenciesList);
    }
  }, [
    paymentType,
    filteredPaymentTypes,
    selectedPaymentType,
    isOpenPayd,
    isKeel,
    integrationType,
  ]);

  useEffect(() => {
    if (caspValues && caspId) {
      const currentCasp = caspValues?.find((item) => item.id === caspId);

      if (currentCasp) {
        const caspCurrencies = currentCasp?.currencies?.map(
          (item) => item.currency,
        );

        if (caspCurrencies?.length) {
          const nextCurrenciesList: Currency[] = [];

          caspCurrencies?.forEach((item) => {
            if (balances?.find((balance) => balance.currencyId === item.id)) {
              nextCurrenciesList.push(item);
            }
          });

          setCurrencies(nextCurrenciesList);
          setAvailableCurrencies(nextCurrenciesList);
        }
      }
    }
  }, [isPrimeVault, caspValues, caspId]);

  useEffect(() => {
    if (casp) {
      setCaspList(
        casp?.map((item) => ({
          name:
            selectedPartner?.caspId && selectedPartner?.caspId === item?.value
              ? `Default from Bank (${item.name})`
              : item.name,
          value: item.value,
        })),
      );
    }
  }, [casp, selectedPartner]);

  useEffect(() => {
    if (selectedExternalOrganizationAccount) {
      if (
        (currencies?.length &&
          !currencies?.find((currency) =>
            selectedExternalOrganizationAccount.currencies.includes(
              currency.name,
            ),
          )) ||
        !currencies?.length
      ) {
        setBindIsNotAvailable(true);
      }
    }
  }, [selectedExternalOrganizationAccount, currencies]);

  const availableCurrenciesVisibile =
    (!isOpenPayd && !isPrimeVault && !isKeel) ||
    (isPrimeVault &&
      (integrationType === 'without-integration' ||
        (integrationType === 'with-integration' &&
          creationType === 'create-new'))) ||
    ((isOpenPayd || isKeel) &&
      (integrationType === 'without-integration' ||
        (integrationType === 'with-integration' &&
          creationType === 'create-new')));

  const currenciesListIsVisible =
    Boolean(
      (isOpenPayd || isKeel) &&
      integrationType === 'with-integration' &&
      accountId,
    ) ||
    Boolean(
      (pspPartnerId &&
        paymentType &&
        !isOpenPayd &&
        !isPrimeVault &&
        !isKeel) ||
      ((isOpenPayd || isKeel) &&
        integrationType === 'without-integration' &&
        Boolean(pspPartnerId && paymentType)) ||
      (isPrimeVault &&
        integrationType === 'without-integration' &&
        Boolean(pspPartnerId && paymentType)),
    ) ||
    Boolean(
      isPrimeVault &&
      integrationType === 'with-integration' &&
      caspId &&
      creationType !== 'bind',
    );

  return (
    <div className={cn.externalCreateForm}>
      <div className={cn.formValues}>
        <Controller
          control={control}
          defaultValue=""
          name="status"
          render={({ field: { value, name, onChange } }) => (
            <Select
              name={name}
              value={value}
              title="Status"
              list={[
                { name: 'Enabled', value: 'enabled' },
                { name: 'Disabled', value: 'disabled' },
              ]}
              onChange={onChange}
              error={errors?.status?.message}
              fullWidth
            />
          )}
        />
      </div>
      <div className={cn.formValues}>
        <Controller
          control={control}
          defaultValue=""
          name="pspPartnerId"
          render={({ field: { value, name, onChange } }) => (
            <Select
              name={name}
              value={value}
              title="Bank"
              placeholder="Select Bank"
              list={
                pspPartners?.map((item) => ({
                  name: item.companyName,
                  value: item.id,
                })) || []
              }
              onChange={(e) => {
                onChange(e);

                if (e.target.value) {
                  const currentPartner = pspPartners?.find(
                    (item) => item.id === e.target.value,
                  );

                  if (currentPartner) {
                    setSelectedPartner(currentPartner);
                  }
                } else {
                  setSelectedPartner(undefined);

                  setIsPrimeVault(false);
                  setIsOpenPayd(false);
                  setIsKeel(false);
                }

                setValue('paymentType', '');
              }}
              error={errors?.pspPartnerId?.message}
              withClearButton
              fullWidth
            />
          )}
        />
      </div>
      {(isOpenPayd || isPrimeVault || isKeel) && (
        <div className={cn.integrationTypeWrapper}>
          <Checkbox
            type="radio"
            name="without-integration"
            checked={integrationType === 'without-integration'}
            title="Without integration"
            onChange={() => {
              setIntegrationType('without-integration');
              setAvailableCurrencies([]);
              setCurrencies([]);
              setValue('currencyId', []);
              setCreationType('create-new');

              if (isPrimeVault && caspId) {
                setValue('caspId', '');
              }
            }}
            labelClassName={cn.withMargin}
          />
          <Checkbox
            type="radio"
            name="with-integration"
            checked={integrationType === 'with-integration'}
            title="With integration"
            onChange={() => {
              setIntegrationType('with-integration');
              setAvailableCurrencies([]);
              setCurrencies([]);
              setValue('currencyId', []);
              setCreationType('create-new');

              if (isPrimeVault && caspId) {
                setValue('caspId', '');
              }
            }}
          />
        </div>
      )}
      <div className={cn.formValues}>
        <Controller
          control={control}
          defaultValue=""
          name="accountId"
          render={({ field: { value, name, onChange } }) => (
            <Autocomplete
              name={name}
              value={value}
              title="Customer Account"
              placeholder="Select Account"
              list={
                accounts?.map((item) => ({
                  name: item.description,
                  value: item.id,
                  suffix: item.fullNumber,
                })) ||
                [] ||
                []
              }
              onChange={onChange}
              error={errors?.accountId?.message}
              withSuffix
              withCopySuffix
              withClearButton
              fullWidth
            />
          )}
        />
      </div>
      {(((isOpenPayd || isKeel) && integrationType === 'with-integration') ||
        isPrimeVault) && (
        <div className={cn.creationTypeWrapper}>
          <div className={cn.creationTypeSelector}>
            <Checkbox
              type="radio"
              name="creationTypeNew"
              title="New Account"
              checked={creationType === 'create-new'}
              onChange={() => setCreationType('create-new')}
              labelClassName={cn.withMargin}
            />
            <Checkbox
              type="radio"
              name="creationTypeBind"
              title="Bind Account"
              checked={creationType === 'bind'}
              onChange={() => {
                setCreationType('bind');

                if (isPrimeVault) {
                  setIntegrationType('with-integration');
                }
              }}
              labelClassName={cn.withMargin}
              disabled={
                !activePermissions?.includes(
                  PermissionEnum.PSP_INTEGRATION_ACCOUNT_ADD_ACTION,
                ) ||
                !activePermissions?.includes(
                  PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
                )
              }
            />
          </div>
          {creationType === 'bind' && (
            <>
              {(isOpenPayd || isKeel) && !pspIntegrationOrganization && (
                <button
                  type="button"
                  className={cn.linkExternalOrganizationButton}
                  onClick={() => {
                    setBindOrganizationPopupVisible(true);
                    setPanelIsOpen(false);
                  }}
                >
                  Link External Customer
                </button>
              )}
              {(pspIntegrationOrganization &&
                !accountId &&
                (isOpenPayd || isKeel)) ||
                (isPrimeVault && !accountId && (
                  <div className={cn.selectCustomerAccountHint}>
                    Select Customer Account
                  </div>
                ))}
              {pspIntegrationOrganization && (isOpenPayd || isKeel) && (
                <>
                  {accountId && (
                    <div className={cn.selectExternalAccountWrapper}>
                      <Autocomplete
                        name="externalOrganizationAccount"
                        value={selectedExternalOrganizationAccount?.id}
                        list={organizationPspExternalAccountsList
                          ?.filter((item) => item)
                          ?.map((item) => ({
                            name: item?.externalId,
                            value: item?.id,
                            description: item?.currencies?.join(' '),
                          }))}
                        placeholder="Select account"
                        isLoading={
                          externalAccountsIsLoading ||
                          externalAccountsIsFetching
                        }
                        onChange={(e) => {
                          if (e.target.value) {
                            const currentAccount =
                              organizationPspExternalAccountsList?.find(
                                (item) => item.id === e.target.value,
                              );

                            if (currentAccount) {
                              setSelectedExternalOrganizationAccount(
                                currentAccount,
                              );
                            }
                          } else {
                            setSelectedExternalOrganizationAccount(undefined);
                          }
                        }}
                        onClearClick={() =>
                          setSelectedExternalOrganizationAccount(undefined)
                        }
                        withClearButton
                      />
                    </div>
                  )}
                  {bindIsNotAvailalbe && (
                    <div className={cn.currenciesNotAvailable}>
                      The account currency does not match the selected internal
                      account for linking.
                    </div>
                  )}
                </>
              )}
              {isPrimeVault && (
                <>
                  <div className={cn.selectExternalAccountWrapper}>
                    <InputText
                      name="primeVaultExternalId"
                      value={primeVaultExternalId}
                      placeholder="Enter external address"
                      onChange={(e) => setPrimeVaultExternalId(e.target.value)}
                    />
                  </div>
                  <div className={cn.selectExternalAccountWrapper}>
                    <Controller
                      control={control}
                      defaultValue=""
                      name="caspId"
                      render={({ field: { value, name, onChange } }) => (
                        <Select
                          name={name}
                          value={value}
                          title="CASP"
                          placeholder="Select CASP"
                          list={casp}
                          onChange={(e) => {
                            onChange(e);
                          }}
                          error={errors?.caspId?.message}
                          fullWidth
                        />
                      )}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
      {((!isOpenPayd && !isPrimeVault && !isKeel) ||
        (isPrimeVault &&
          creationType === 'create-new' &&
          integrationType === 'without-integration') ||
        ((isOpenPayd || isKeel) &&
          integrationType === 'without-integration')) && (
        <div className={cn.formValues}>
          <Controller
            control={control}
            defaultValue=""
            name="iban"
            render={({ field: { value, name, onChange } }) => (
              <InputText
                name={name}
                value={value}
                title="External Account number"
                placeholder="Enter number"
                onChange={onChange}
                error={errors?.iban?.message}
              />
            )}
          />
        </div>
      )}
      {((!isOpenPayd && !isPrimeVault && !isKeel) ||
        ((isOpenPayd || isKeel) && integrationType === 'without-integration') ||
        (isPrimeVault &&
          creationType !== 'bind' &&
          integrationType === 'without-integration')) && (
        <div className={cn.formValues}>
          <Controller
            control={control}
            defaultValue=""
            name="paymentType"
            render={({ field: { value, name, onChange } }) => (
              <Select
                name={name}
                value={value}
                title="Payment Type"
                placeholder="Select Payment Type"
                list={
                  filteredPaymentTypes?.map((item) => ({
                    name: item.name,
                    value: item.paymentType,
                  })) || []
                }
                onChange={(e) => {
                  onChange(e);

                  const currentPaymentType = filteredPaymentTypes?.find(
                    (item) => item.paymentType === e.target.value,
                  );

                  if (currentPaymentType) {
                    setSelectedPaymentType(currentPaymentType);
                  }
                }}
                error={errors?.paymentType?.message}
                disabled={!pspPartnerId || !accountId}
                withClearButton
                fullWidth
              />
            )}
          />
        </div>
      )}
      {((paymentType &&
        paymentType === PaymentTypeProps.CRYPTO_ASSET_PAYMENT) ||
        (isPrimeVault &&
          integrationType === 'with-integration' &&
          creationType !== 'bind')) && (
        <div className={cn.formValues}>
          <Controller
            control={control}
            defaultValue=""
            name="caspId"
            render={({ field: { value, name, onChange } }) => (
              <Select
                name={name}
                value={value}
                title="CASP"
                placeholder="Select CASP"
                list={caspList}
                onChange={onChange}
                error={errors?.caspId?.message}
                disabled={!accountId}
                withClearButton
                fullWidth
              />
            )}
          />
        </div>
      )}
      {availableCurrenciesVisibile && (
        <div className={cn.currenciesWrapper}>
          <div className={cn.currenciesTitle}>Available currencies</div>
          {(isOpenPayd || isKeel) &&
            integrationType === 'with-integration' &&
            !accountId && (
              <div className={cn.selectValues}>Select Customer Account</div>
            )}
          {(((!pspPartnerId || !paymentType) &&
            !isOpenPayd &&
            !isPrimeVault &&
            !isKeel) ||
            ((isOpenPayd || isKeel) &&
              integrationType === 'without-integration' &&
              Boolean(!pspPartnerId || !paymentType)) ||
            (isPrimeVault &&
              integrationType === 'without-integration' &&
              Boolean(!pspPartnerId || !paymentType))) && (
            <div className={cn.selectValues}>Select Bank and Payment Type</div>
          )}
          {isPrimeVault &&
            integrationType === 'with-integration' &&
            !caspId &&
            creationType !== 'bind' && (
              <div className={cn.selectValues}>Select CASP</div>
            )}
          {!currencies?.length &&
            pspPartnerId &&
            accountId &&
            ((!isOpenPayd && !isKeel && paymentType) ||
              ((isOpenPayd || isKeel) &&
                integrationType === 'without-integration' &&
                paymentType) ||
              (!isPrimeVault && paymentType) ||
              (isPrimeVault &&
                integrationType === 'without-integration' &&
                paymentType)) && (
              <div className={cn.selectValues}>
                Based on the account currencies and the bank integration, there
                are no available currencies for creating an external account
              </div>
            )}
          {balancesIsLoading && (
            <div className={cn.balancesLoader}>
              Loading <IconAnimatedDots width={26} height={26} />
            </div>
          )}
          {currenciesListIsVisible && currencies && currencies?.length > 0 && (
            <>
              <div className={cn.searchWrapper}>
                <IconSearch className={cn.searchIcon} width={16} height={16} />
                <InputText
                  name="search"
                  size={InputSize.SMALL}
                  placeholder="Enter currency"
                  className={cn.searchInput}
                  value={searchValue}
                  onChange={(e) => onSearch(e.target.value)}
                />
              </div>
              <div className={cn.selectAllCheckboxWrapper}>
                <Checkbox
                  name="select-all"
                  checked={Boolean(currencyId?.length === currencies.length)}
                  type="checkbox"
                  title="Select all"
                  onChange={(e) => {
                    const currenciesList: string[] = e.target.checked
                      ? currencies?.map((item) => item.id)
                      : [];

                    setValue('currencyId', currenciesList);
                  }}
                />
              </div>

              <Controller
                control={control}
                name="currencyId"
                render={({ field: { value, name, onChange } }) => (
                  <div>
                    {currencies?.map((item) => (
                      <div className={cn.checkboxWrapper} key={item.id}>
                        <Checkbox
                          name={`${name}-${item.id}`}
                          type="checkbox"
                          checked={Boolean(
                            value?.find((currency) => currency === item.id),
                          )}
                          title={item.name}
                          onChange={(e) => {
                            let nextCurrencies = ([] as string[]).concat(value);

                            if (e.target.checked) {
                              nextCurrencies.push(item.id);
                            } else {
                              nextCurrencies = nextCurrencies?.filter(
                                (currency) => currency !== item.id,
                              );
                            }

                            onChange(nextCurrencies);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ExternalAccountCreateForm;
